/**
 * The device side of sync: push the outbox, then pull the facility's changes.
 *
 * Invariants (each one tested in e2e/sync.e2e.test.ts with the network cut at every step):
 * - An op is marked 'sent' BEFORE it is sent. A 'sent' op is only ever retried byte-for-byte
 *   (same opId), so the server's idempotency record makes a retry harmless.
 * - An op leaves the outbox only when the server has answered for it (applied, merged, rejected).
 *   A rejected op moves to the failed list; it is never silently dropped.
 * - Only one op per record is in flight. A later edit waits for the earlier one's outcome and is
 *   then based on it, so concurrent edits merge on the server instead of overwriting.
 * - The pull cursor is saved in the same local transaction as the records it covers.
 */
import { deepEqual, patientIdOf, validateResource, } from '@assist/core';
import { aad, recordKey, stripMeta } from './device.js';
import { AuthFailure, DeviceRevokedError, NeedsOnlineLoginError, NetworkError } from './errors.js';
/** Set once the device has pulled the whole feed again after the 2026-10-12 server fix. */
const REPULL_MARK = 'repull-2026-10-12';
export const PUSH_BATCH = 50;
export const PULL_PAGE = 500;
/** An op deferred this many times (its referenced record never arrived) is moved to the failed list. */
export const MAX_DEFERRALS = 50;
export class SyncEngine {
    s;
    report = {
        pushed: 0,
        applied: 0,
        merged: 0,
        rejected: 0,
        deferred: 0,
        pulled: 0,
        conflicts: [],
        offline: false,
    };
    constructor(s) {
        this.s = s;
    }
    get db() {
        return this.s.db;
    }
    get mutex() {
        return this.s.device.mutex;
    }
    async run(t) {
        const token = this.s.auth?.token;
        if (!token)
            throw new NeedsOnlineLoginError('Sign in online to sync');
        try {
            await this.pushAll(t, token);
            await this.pullAll(t, token);
            await this.db.meta.put({ k: 'lastSyncAt', v: Date.now() });
        }
        catch (e) {
            if (e instanceof NetworkError) {
                this.report.offline = true;
                this.report.error = e.message;
                return this.report;
            }
            if (e instanceof AuthFailure) {
                if (e.code === 'device_revoked') {
                    await this.s.device.wipe();
                    throw new DeviceRevokedError();
                }
                if (e.code === 'session_expired' || e.code === 'unauthenticated') {
                    this.s.auth = null;
                    await this.db.meta.delete(`session:${this.s.user.id}`);
                    throw new NeedsOnlineLoginError('Your session ended. Sign in online again to sync; your work is kept.');
                }
            }
            throw e;
        }
        return this.report;
    }
    // ------------------------------------------------------------------ push
    async pushAll(t, token) {
        for (let round = 0; round < 10_000; round++) {
            const batch = await this.mutex.run(() => this.takeBatch());
            if (!batch.length)
                return;
            let resp;
            try {
                resp = await t.push(token, {
                    ops: batch.map((b) => ({
                        opId: b.row.opId,
                        resource: b.payload.resource,
                        baseVersionId: b.row.baseVersionId,
                        baseBody: b.payload.baseBody,
                    })),
                });
            }
            catch (e) {
                await this.mutex.run(async () => {
                    for (const b of batch) {
                        await this.db.outbox.update(b.row.opId, {
                            attempts: b.row.attempts + 1,
                            lastError: e instanceof Error ? e.message : String(e),
                        });
                    }
                });
                throw e;
            }
            this.report.pushed += batch.length;
            const progressed = await this.mutex.run(() => this.applyResults(resp.results));
            if (!progressed)
                return; // everything deferred: try again next sync
        }
    }
    /** The oldest op of each record, whose predecessor (if any) has resolved. Marked 'sent' now. */
    async takeBatch() {
        const ops = await this.db.outbox.orderBy('seq').toArray();
        const ids = new Set(ops.map((o) => o.opId));
        const keys = new Set();
        const chosen = [];
        for (const o of ops) {
            if (chosen.length >= PUSH_BATCH)
                break;
            if (keys.has(o.key))
                continue;
            keys.add(o.key);
            if (o.dependsOn && ids.has(o.dependsOn))
                continue;
            chosen.push(o);
        }
        const toMark = chosen
            .filter((o) => o.state === 'queued')
            .map((o) => ({ ...o, state: 'sent' }));
        if (toMark.length)
            await this.db.outbox.bulkPut(toMark);
        return Promise.all(chosen.map(async (row) => ({
            row,
            payload: await this.s.openSealed(row.keyId, row.payload, aad.op(row.opId)),
        })));
    }
    async applyResults(results) {
        let progressed = false;
        for (const r of results) {
            const op = await this.db.outbox.get(r.opId);
            if (!op)
                continue;
            if (r.status === 'deferred') {
                this.report.deferred++;
                const deferrals = op.deferrals + 1;
                if (deferrals >= MAX_DEFERRALS) {
                    await this.fail(op, `Gave up: ${r.error ?? 'a referenced record never arrived'}`);
                    progressed = true;
                }
                else {
                    await this.db.outbox.update(op.opId, { deferrals, lastError: r.error });
                }
                continue;
            }
            progressed = true;
            if (r.status === 'rejected') {
                this.report.rejected++;
                await this.fail(op, r.error ?? 'Rejected by the server');
                continue;
            }
            // applied | merged
            if (r.status === 'applied')
                this.report.applied++;
            else
                this.report.merged++;
            for (const c of r.conflicts ?? [])
                this.report.conflicts.push({ ...c, key: op.key });
            await this.confirm(op, r);
        }
        if (progressed)
            this.s.invalidateCaches();
        return progressed;
    }
    /** The server holds `r.resource` as version `r.versionId` for this op's record. */
    async confirm(op, r) {
        const server = r.resource;
        const version = r.versionId ?? null;
        const sent = await this.s.openSealed(op.keyId, op.payload, aad.op(op.opId));
        const others = (await this.db.outbox.where('key').equals(op.key).toArray()).filter((o) => o.opId !== op.opId);
        // A later edit was built on what WE sent. It may claim the new version as its base only if the
        // server stored exactly that; otherwise it must be merged on the server against its own base.
        const clean = r.status === 'applied' && deepEqual(stripMeta(server), stripMeta(sent.resource));
        const rec = await this.db.records.get(op.key);
        const baseSealed = await this.s.sealNow(server, aad.base(op.key));
        const curSealed = others.length
            ? rec
                ? await this.s.reseal(rec.keyId, rec.cur, aad.cur(op.key))
                : null
            : await this.s.sealNow(server, aad.cur(op.key));
        await this.db.transaction('rw', [this.db.records, this.db.outbox], async () => {
            for (const o of others) {
                if (o.dependsOn === op.opId) {
                    const { dependsOn: _d, ...rest } = o;
                    await this.db.outbox.put({ ...rest, baseVersionId: clean ? version : null });
                }
            }
            await this.db.outbox.delete(op.opId);
            if (rec || curSealed) {
                const { error: prevError, ...prev } = (rec ?? {});
                const row = {
                    ...prev,
                    serverVersion: version,
                    pending: others.length ? 1 : 0,
                    updatedAt: Date.now(),
                    keyId: this.s.keyId,
                    cur: (curSealed ?? rec?.cur),
                    base: baseSealed,
                };
                if (others.length && prevError)
                    row.error = prevError;
                await this.db.records.put(row);
            }
        });
    }
    /** Move an op to the failed list. Its record keeps the local content, flagged with the error. */
    async fail(op, error) {
        const p = await this.s.openSealed(op.keyId, op.payload, aad.op(op.opId));
        const deadPayload = await this.s.sealNow(p, aad.dead(op.opId));
        const rec = await this.db.records.get(op.key);
        const base = rec?.base
            ? await this.s.openSealed(rec.keyId, rec.base, aad.base(op.key))
            : null;
        const dependents = (await this.db.outbox.where('key').equals(op.key).toArray()).filter((o) => o.dependsOn === op.opId);
        const rebased = await Promise.all(dependents.map(async (d) => {
            const dp = await this.s.openSealed(d.keyId, d.payload, aad.op(d.opId));
            const { dependsOn: _x, ...rest } = d;
            return {
                ...rest,
                keyId: this.s.keyId,
                baseVersionId: rec?.serverVersion ?? null,
                payload: await this.s.sealNow({ resource: dp.resource, baseBody: base }, aad.op(d.opId)),
            };
        }));
        await this.db.transaction('rw', [this.db.records, this.db.outbox, this.db.deadletter], async () => {
            await this.db.deadletter.put({
                opId: op.opId,
                key: op.key,
                at: Date.now(),
                error,
                keyId: this.s.keyId,
                payload: deadPayload,
            });
            await this.db.outbox.delete(op.opId);
            for (const d of rebased)
                await this.db.outbox.put(d);
            if (rec)
                await this.db.records.update(op.key, { error });
        });
    }
    // ------------------------------------------------------------------ pull
    async pullAll(t, token) {
        // Before server fix 2026-10-12 a pull could skip records (the server sorted the sequence as
        // text). Pull the whole feed again once: records already held at the same version are skipped.
        if (!(await this.db.meta.get(REPULL_MARK))) {
            await this.db.transaction('rw', [this.db.meta], async () => {
                await this.db.meta.put({ k: 'cursor', v: '0' });
                await this.db.meta.put({ k: REPULL_MARK, v: 1 });
            });
        }
        for (let page = 0; page < 100_000; page++) {
            const since = (await this.db.meta.get('cursor'))?.v ?? '0';
            const resp = await t.pull(token, since, PULL_PAGE);
            await this.mutex.run(() => this.applyPull(resp));
            this.report.pulled += resp.changes.length;
            if (!resp.more)
                return;
        }
    }
    async applyPull(resp) {
        const rows = [];
        for (const ch of resp.changes) {
            const v = validateResource(ch.resource);
            if (!v.ok)
                continue; // the server validated it; a local schema mismatch must not block sync
            const res = v.resource;
            const server = ch.resource;
            const key = recordKey(res.resourceType, res.id);
            const version = server.meta?.versionId ?? null;
            const rec = await this.db.records.get(key);
            if (rec?.serverVersion && version && Number(version) <= Number(rec.serverVersion))
                continue;
            const hasOps = (await this.db.outbox.where('key').equals(key).count()) > 0;
            rows.push({
                key,
                type: res.resourceType,
                id: res.id,
                patientId: rec?.patientId ?? patientIdOf(res),
                serverVersion: version,
                pending: hasOps ? 1 : 0,
                updatedAt: Date.now(),
                keyId: this.s.keyId,
                cur: hasOps && rec
                    ? await this.s.reseal(rec.keyId, rec.cur, aad.cur(key))
                    : await this.s.sealNow(server, aad.cur(key)),
                base: await this.s.sealNow(server, aad.base(key)),
                ...(hasOps && rec?.error ? { error: rec.error } : {}),
            });
        }
        await this.db.transaction('rw', [this.db.records, this.db.meta], async () => {
            if (rows.length)
                await this.db.records.bulkPut(rows);
            await this.db.meta.put({ k: 'cursor', v: resp.cursor });
        });
        if (rows.length)
            this.s.invalidateCaches();
    }
}
//# sourceMappingURL=sync.js.map